<script setup lang="ts">
import {storeToRefs} from "pinia";
import {onBeforeMount, onErrorCaptured, ref, watch} from "vue";
import {RouteLocationRaw, RouterView, useRouter} from "vue-router";

import type {Ref} from "vue";
import AppLayout from "@/components/layout/AppLayout.vue";
import ProgressLoader from "@/components/layout/ProgressLoader.vue";
import {useAppStore, useConfigStore} from "@/store";
import {error} from "@/utils/toast.ts";
import {getRouteXDashboard} from "@/views/Auth/Dashboard.vue";
import {getRouteSignupVerifyPhone} from "@/views/Guest/Signup/Onboard/PhoneVerify.vue";
import {getRouteSignupPhone} from "@/views/Guest/Signup/Onboard/Phone.vue";
import {getRouteSignupVerifyEmail} from "@/views/Guest/Signup/Onboard/EmailVerify.vue";
import {PersonDTO} from "@/repositories/dto/user/PersonDTO.ts";
import Spinner from "@/components/icons/Spinner.vue";
import IsOnlineWidget from "@/widgets/IsOnlineWidget.vue";
import {OnboardingStep} from "@/store/onboardingStore.ts";
import usePersonStore from "@/store/personStore.ts";
import {IUserStep} from "@/repositories/dto/user/IPersonState.ts";

const appStore = useAppStore();
const configStore = useConfigStore();
const personStore = usePersonStore();

const {getIsLoading} = storeToRefs(appStore);
const {currentStep, person} = storeToRefs(personStore);

const isLoading: Ref<boolean> = ref<boolean>(false);

const ready: Ref<boolean> = ref<boolean>(false);

onBeforeMount(async () => {
  appStore.beginDeterminateLoading();

  isLoading.value = true;
  await configStore.init();
  await personStore.init();

  isLoading.value = false;

  appStore.endDeterminateLoading();
  ready.value = true;
});

const router = useRouter();

watch([
  person,
  currentStep,
], async ([personNew, currentStep], [personOld,]) => {
  const isAuthenticated = personNew !== null;
  if (!isAuthenticated && personOld !== null) {
    // When user logs out, redirect to Dashboard (guest view)
    await router.push(getRouteXDashboard());
    return;
  }

  if (isAuthenticated) {
    await doRouting(personNew, currentStep);
  }
});

async function doRouting(_person: PersonDTO, currentStep: IUserStep | null) {
  // Only redirect if currentStep explicitly requires verification action
  // If DONE or no match, don't redirect (user can stay where they are)
  if (currentStep !== null) {
    let route: RouteLocationRaw | null = null;

    switch (currentStep?.key) {
      case OnboardingStep.EMAIL_VERIFY:
        route = getRouteSignupVerifyEmail();
        break;
      case OnboardingStep.PHONE:
        route = getRouteSignupPhone();
        break;
      case OnboardingStep.PHONE_VERIFY:
        route = getRouteSignupVerifyPhone();
        break;
      case OnboardingStep.DONE:
        // Verification complete, no redirect needed
        return;
      default:
        // Unknown step, no redirect needed
        return;
    }

    if (route !== null) {
      await router.push(route);
    }
  }
}

if (person.value !== null) {
  doRouting(person.value, currentStep.value)
}

// Top level error handler
onErrorCaptured((e: Error) => {
  error("Error " + e.message); // ToastOptions
});
</script>

<template>
  <ProgressLoader v-if="getIsLoading"/>

  <AppLayout class="min-h-screen">
    <RouterView v-if="ready"/>
  </AppLayout>

  <div v-if="isLoading" class="absolute top-0 left-0 w-full h-full backdrop-blur-sm bg-white/30 text-center">
    <div class="flex justify-center items-center w-full h-full">
      <Spinner></Spinner>
    </div>
  </div>

  <IsOnlineWidget/>
</template>

<style scoped></style>
